import React, { useEffect, useMemo, useState } from "react";

import {
  ChevronDown,
  ChevronLeft,
  Download,
  FileText,
  ScrollText,
  X,
} from "lucide-react";
import { Cell, Pie, PieChart } from "recharts";

import { useAppDispatch } from "../../Store/Hooks";
import { fetchAccountAssessmentResponses } from "../../Store/Reducers/AssessmentSlice";
import {
  formatAssessmentDateTimeUtc,
  formatAssessmentDateUtc,
} from "../../Utils/Helper/assessmentDateUtils";
import {
  getLatestLevelUpdateAt,
  getLevelUpdateLog,
  levelUpdateEventLabel,
  sortLevelUpdateLogNewestFirst,
} from "../../Utils/Helper/levelUpdateLogUtils";
import {
  getAccountLevelVerboseLabel,
  getLevelCompactName,
  getLevelDisplayName,
} from "../../Utils/Helper/levelUtils";

type Executive = {
  id?: string;
  user_id?: string;
  name?: string;
  full_name?: string;
  email?: string;
  role?: string;
};

type Account = {
  id?: string;
  account_id?: string;
  name?: string;
  account_name?: string;
  totalAssigned?: number;
  requests_sent?: number;
  responses?: number;
  responses_received?: number;
  finalLevel?: string | null;
  final_level?: string | null;
  medianLevel?: string | null;
  median_level?: string | null;
  override?: {
    override_level?: string | null;
  };
  executives?: Executive[];
};

type AssessmentLike = {
  id?: string;
  assessment_id?: string;
  name?: string;
  title?: string;
  description?: string | null;
  category?: string | null;
  dueDate?: string | null;
  due_date?: string | null;
  due_on?: string | null;
  published_at?: string | null;
  created_at?: string | null;
  start_date?: string | null;
  launched_at?: string | null;
  launchedAt?: string | null;
  createdBy?: string | null;
  created_by_name?: string | null;
  created_by_user?: {
    name?: string;
  };
  accounts?: Account[];
  notes?: {
    level_update_log?: Array<{
      id?: string;
      at?: string | null;
      type?: string | null;
      account_id?: string | null;
      account_name?: string | null;
      actor_name?: string | null;
      level?: string | null;
      rationale?: string | null;
    }> | null;
  } | null;
};

type ResponseItem = {
  id?: string;
  executiveName?: string;
  executiveEmail?: string;
  submittedAt?: string;
  calculatedLevel?: string | null;
  rationale?: string | null;
  responses?: Record<string, string>;
};

type AccountResponseState = {
  loading: boolean;
  error: string | null;
  responses: ResponseItem[];
};

type FlatReportRow = {
  account: Account;
  finalLevel: string | null;
  status: "Complete" | "Incomplete";
  responses: ResponseItem[];
};

interface FlatReportModalProps {
  isOpen: boolean;
  assessment: AssessmentLike | null;
  accounts: Account[];
  getLevel: (account: Account) => string | null;
  getLevelLabel: (level: string | number | null | undefined) => string;
  getSponsor: (account: Account) => string;
  onClose: () => void;
}

interface ConsolidatedReportModalProps {
  isOpen: boolean;
  assessment: AssessmentLike | null;
  organizationLevels?: Array<{
    level_code: string;
    name?: string;
    description?: string;
  }>;
  onClose: () => void;
  onViewAccount?: (assessment: AssessmentLike, account: Account) => void;
}

const SUMMARY_PIE_COLORS = [
  "#94a3b8",
  "#f87171",
  "#fb923c",
  "#fbbf24",
  "#60a5fa",
  "#4ade80",
];

const getSummaryPieColor = (level: string | number) => {
  const num = parseInt(String(level || "").replace(/[^0-9]/g, ""), 10);
  return SUMMARY_PIE_COLORS[isNaN(num) ? 0 : num % SUMMARY_PIE_COLORS.length];
};

const getLevelNumber = (level: string | number | null | undefined): number => {
  if (level === null || level === undefined) return 0;
  const num = parseInt(String(level).replace(/[^0-9]/g, ""), 10);
  return Number.isNaN(num) ? 0 : num;
};

const getLevelKey = (
  level: string | number | null | undefined
): string | null => {
  if (level === null || level === undefined || String(level).trim() === "") {
    return null;
  }
  return `L${getLevelNumber(level)}`;
};

const resolveAccountLevel = (acc: Account): string | null => {
  const overrideLevel = acc.override?.override_level?.trim();
  if (overrideLevel) return overrideLevel;

  return (
    acc.finalLevel ||
    acc.final_level ||
    acc.medianLevel ||
    acc.median_level ||
    null
  );
};

const getLevelPillClass = (
  level: string | number | null | undefined
): string => {
  const n = getLevelNumber(level);
  if (n >= 4) return "bg-blue-100 text-blue-800";
  if (n === 3) return "bg-yellow-100 text-yellow-800";
  if (n <= 2) return "bg-orange-100 text-orange-800";
  return "bg-gray-100 text-gray-700";
};

const exportFlatCSV = (
  assessment: AssessmentLike,
  accountsData: FlatReportRow[]
) => {
  const csvEscapedQuote = String.fromCharCode(34, 34);
  const fmtLabel = (key: string) =>
    key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  const escape = (v: unknown) =>
    `"${String(v ?? "").replace(/"/g, csvEscapedQuote)}"`;

  const extraKeys = new Set<string>();
  accountsData.forEach(({ responses }) =>
    responses.forEach(r => {
      if (r.responses && typeof r.responses === "object") {
        Object.keys(r.responses).forEach(k => {
          if (k !== "maturity_level" && k !== "rationale") extraKeys.add(k);
        });
      }
    })
  );
  const extras = Array.from(extraKeys);

  const header = [
    "Assessment",
    "Account",
    "Final Account Level",
    "Status",
    "Sponsor",
    "Sponsor Email",
    "Sponsor Level",
    "Submitted",
    "Rationale",
    ...extras.map(fmtLabel),
  ];

  const rows = [header.map(escape).join(",")];
  accountsData.forEach(({ account, finalLevel, status, responses }) => {
    const assessmentTitle = assessment.title || assessment.name || "";
    const accountName = account.name || account.account_name || "";

    if (responses.length === 0) {
      rows.push(
        [
          assessmentTitle,
          accountName,
          getLevelCompactName(finalLevel || "") || "—",
          status,
          "—",
          "—",
          "—",
          "—",
          "—",
          ...extras.map(() => "—"),
        ]
          .map(escape)
          .join(",")
      );
    } else {
      responses.forEach(r => {
        const rationale = r.rationale || r.responses?.rationale || "";
        rows.push(
          [
            assessmentTitle,
            accountName,
            getLevelCompactName(finalLevel || "") || "—",
            status,
            r.executiveName || "",
            r.executiveEmail || "",
            getLevelCompactName(r.calculatedLevel || "") || "—",
            r.submittedAt ? formatAssessmentDateTimeUtc(r.submittedAt) : "",
            rationale,
            ...extras.map(k => r.responses?.[k] ?? ""),
          ]
            .map(escape)
            .join(",")
        );
      });
    }
  });

  const csv = rows.join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(assessment.title || assessment.name || "assessment")
    .replace(/[^a-z0-9]/gi, "-")
    .toLowerCase()}-flat-report.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const FlatReportModal: React.FC<FlatReportModalProps> = ({
  isOpen,
  assessment,
  accounts,
  getLevel,
  getLevelLabel,
  getSponsor,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [responseMap, setResponseMap] = useState<
    Record<string, AccountResponseState>
  >({});
  const [fetchDone, setFetchDone] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const getAccountId = (account: Account) =>
    String(account.id || account.account_id || "");

  useEffect(() => {
    if (!isOpen || !assessment) return;
    setResponseMap({});
    setFetchDone(false);
    setCollapsed({});

    const complete = accounts.filter(
      a =>
        (a.totalAssigned || a.requests_sent || 0) > 0 &&
        (a.responses || a.responses_received || 0) ===
          (a.totalAssigned || a.requests_sent || 0)
    );
    const incomplete = accounts.filter(
      a =>
        !(
          (a.totalAssigned || a.requests_sent || 0) > 0 &&
          (a.responses || a.responses_received || 0) ===
            (a.totalAssigned || a.requests_sent || 0)
        )
    );

    const fetchAll = async () => {
      const init: Record<string, AccountResponseState> = {};
      [...complete, ...incomplete].forEach(a => {
        init[getAccountId(a)] = { loading: true, error: null, responses: [] };
      });
      setResponseMap({ ...init });

      await Promise.allSettled(
        complete.map(async acct => {
          try {
            const assessmentId = assessment.id || assessment.assessment_id;
            const accountId = acct.id || acct.account_id;
            const data = await dispatch(
              fetchAccountAssessmentResponses({
                assessmentId: String(assessmentId || ""),
                accountId: String(accountId || ""),
              })
            ).unwrap();
            const responseItems = Array.isArray((data as any)?.responses)
              ? (data as any).responses
              : [];
            const responses = responseItems
              .filter((r: any) => !r?.is_draft)
              .map((r: any) => {
                const accountExecs = acct.executives || [];
                const matchedExec = accountExecs.find(
                  e =>
                    String((e as any).id || "") ===
                      String(r.executive_id || "") ||
                    String((e as any).user_id || "") ===
                      String(r.executive_id || "")
                );
                const responsePayload = r.responses || {};
                return {
                  id: r.id,
                  executiveName:
                    r.executive?.name ||
                    r.executive?.full_name ||
                    matchedExec?.name ||
                    matchedExec?.full_name ||
                    "Unknown Executive",
                  executiveEmail:
                    r.executive?.email || matchedExec?.email || "",
                  submittedAt: r.submitted_at || r.updated_at,
                  calculatedLevel:
                    assessment.category === "short" &&
                    responsePayload?.maturity_level
                      ? responsePayload.maturity_level
                      : r.calculatedLevel || null,
                  rationale: responsePayload?.rationale || null,
                  responses: responsePayload,
                };
              });
            setResponseMap(prev => ({
              ...prev,
              [getAccountId(acct)]: {
                loading: false,
                error: null,
                responses,
              },
            }));
          } catch {
            setResponseMap(prev => ({
              ...prev,
              [getAccountId(acct)]: {
                loading: false,
                error: "Failed to load",
                responses: [],
              },
            }));
          }
        })
      );

      setResponseMap(prev => {
        const next = { ...prev };
        incomplete.forEach(a => {
          next[getAccountId(a)] = {
            loading: false,
            error: null,
            responses: [],
          };
        });
        return next;
      });
      setFetchDone(true);
    };

    fetchAll();
  }, [isOpen, assessment, accounts, dispatch]);

  if (!isOpen || !assessment) return null;

  const completeAccounts = accounts.filter(
    a =>
      (a.totalAssigned || a.requests_sent || 0) > 0 &&
      (a.responses || a.responses_received || 0) ===
        (a.totalAssigned || a.requests_sent || 0)
  );
  const incompleteAccounts = accounts.filter(
    a =>
      !(
        (a.totalAssigned || a.requests_sent || 0) > 0 &&
        (a.responses || a.responses_received || 0) ===
          (a.totalAssigned || a.requests_sent || 0)
      )
  );

  const toggleCollapse = (id: string) =>
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  const buildExportData = (): FlatReportRow[] => [
    ...completeAccounts.map(a => ({
      account: a,
      finalLevel: getLevel(a),
      status: "Complete" as const,
      responses: responseMap[getAccountId(a)]?.responses || [],
    })),
    ...incompleteAccounts.map(a => ({
      account: a,
      finalLevel: getLevel(a),
      status: "Incomplete" as const,
      responses: responseMap[getAccountId(a)]?.responses || [],
    })),
  ];

  const totalLoading = Object.values(responseMap).filter(v => v.loading).length;

  const AccountCard: React.FC<{
    account: Account;
    status: "Complete" | "Incomplete";
  }> = ({ account, status }) => {
    const isIncomplete = status === "Incomplete";
    const finalLevel = getLevel(account);
    const sponsor = getSponsor(account);
    const accountKey = getAccountId(account);
    const state = responseMap[accountKey] || {
      loading: true,
      error: null,
      responses: [],
    };
    const isCollapsed = collapsed[accountKey];

    return (
      <div
        className={`border rounded-xl overflow-hidden ${
          isIncomplete ? "border-amber-200" : "border-gray-200"
        }`}
      >
        <button
          onClick={() => toggleCollapse(accountKey)}
          className={`w-full flex items-center gap-3 px-5 py-4 text-left ${
            isIncomplete ? "bg-amber-50" : "bg-gray-50"
          } hover:brightness-95 transition-all`}
        >
          <ChevronDown
            size={12}
            strokeWidth={2.5}
            className={`flex-shrink-0 text-gray-400 transition-transform ${
              isCollapsed ? "-rotate-90" : ""
            }`}
          />
          <div className="flex-1 min-w-0">
            <div
              className={`text-sm font-semibold truncate ${
                isIncomplete ? "text-amber-800" : "text-gray-900"
              }`}
            >
              {isIncomplete && <span className="mr-1">⚠</span>}
              {account.name || account.account_name}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              {sponsor} · {account.responses || account.responses_received || 0}
              /{account.totalAssigned || account.requests_sent || 0} responded
            </div>
          </div>
          {finalLevel && (
            <span
              className={`flex-shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                isIncomplete
                  ? "bg-amber-100 text-amber-700 border border-amber-300"
                  : getLevelPillClass(finalLevel)
              }`}
            >
              {isIncomplete
                ? `Prior: ${getLevelLabel(finalLevel)}`
                : getLevelLabel(finalLevel)}
            </span>
          )}
          {state.loading && (
            <span className="text-xs text-gray-400 flex-shrink-0">
              loading…
            </span>
          )}
        </button>

        {!isCollapsed && (
          <div className="divide-y divide-gray-100">
            {state.loading && (
              <div className="px-4 py-3 text-xs text-gray-400 italic">
                Fetching responses…
              </div>
            )}
            {state.error && (
              <div className="px-4 py-3 text-xs text-red-500">
                {state.error}
              </div>
            )}
            {!state.loading && !state.error && state.responses.length === 0 && (
              <div className="px-4 py-3 text-xs text-gray-400 italic">
                No responses submitted yet.
              </div>
            )}
            {!state.loading &&
              state.responses.map((resp, idx) => {
                const lvl = resp.calculatedLevel;
                const rationale = resp.rationale || resp.responses?.rationale;
                const extras = resp.responses
                  ? Object.entries(resp.responses).filter(
                      ([k]) => k !== "maturity_level" && k !== "rationale"
                    )
                  : [];
                const fmtLabel = (k: string) =>
                  k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                return (
                  <div
                    key={resp.id || idx}
                    className="px-5 py-4 grid grid-cols-[260px_1fr] gap-6"
                  >
                    <div className="flex flex-col gap-1 pt-0.5">
                      <div className="text-xs font-semibold text-gray-800">
                        {resp.executiveName}
                      </div>
                      <div className="text-[11px] text-gray-400 break-all">
                        {resp.executiveEmail}
                      </div>
                      {lvl && (
                        <span
                          className={`mt-1 inline-flex self-start items-center px-3 py-1 rounded-full text-xs font-semibold ${getLevelPillClass(
                            lvl
                          )}`}
                        >
                          {getLevelLabel(lvl)}
                        </span>
                      )}
                      {resp.submittedAt && (
                        <div className="text-[11px] text-gray-400 mt-1">
                          {formatAssessmentDateUtc(resp.submittedAt, "US_LONG")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2.5">
                      {rationale ? (
                        <div>
                          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                            Rationale
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {rationale}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">
                          No rationale provided.
                        </p>
                      )}
                      {extras
                        .filter(([, v]) => v)
                        .map(([key, val]) => (
                          <div key={key}>
                            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                              {fmtLabel(key)}
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {val}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col"
        style={{ maxHeight: "calc(100vh - 4rem)" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between flex-shrink-0">
          <div>
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
              Consolidated Responses — All Accounts
            </div>
            <h2 className="text-3xl font-bold text-gray-900 leading-tight">
              {assessment.title || assessment.name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {completeAccounts.length} complete · {incompleteAccounts.length}{" "}
              incomplete · {accounts.length} total
              {!fetchDone && totalLoading > 0 && (
                <span className="ml-2 text-purple-500">
                  loading {totalLoading} remaining…
                </span>
              )}
              {fetchDone && (
                <span className="ml-2 text-green-600">
                  all responses loaded
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {completeAccounts.length > 0 && (
            <>
              <div className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider pb-2 border-b border-gray-100">
                Complete — {completeAccounts.length} account
                {completeAccounts.length !== 1 ? "s" : ""}
              </div>
              {completeAccounts.map(a => (
                <AccountCard
                  key={getAccountId(a)}
                  account={a}
                  status="Complete"
                />
              ))}
            </>
          )}
          {incompleteAccounts.length > 0 && (
            <>
              <div className="text-[13px] font-semibold text-amber-600 uppercase tracking-wider pb-2 border-b border-amber-100 mt-4">
                Incomplete — {incompleteAccounts.length} account
                {incompleteAccounts.length !== 1 ? "s" : ""}
              </div>
              {incompleteAccounts.map(a => (
                <AccountCard
                  key={getAccountId(a)}
                  account={a}
                  status="Incomplete"
                />
              ))}
            </>
          )}
        </div>

        <div className="px-6 py-3.5 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center gap-3">
            <button
              onClick={() => exportFlatCSV(assessment, buildExportData())}
              disabled={!fetchDone}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download size={12} strokeWidth={2} />
              Export CSV {!fetchDone ? "(loading…)" : ""}
            </button>
            <span className="text-xs text-gray-400 italic">
              PDF export coming soon
            </span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const exportSummaryCSV = (
  assessment: AssessmentLike,
  completeAccounts: Account[],
  incompleteAccounts: Account[],
  getLevel: (account: Account) => string | null,
  getSponsor: (account: Account) => string
) => {
  const csvEscapedQuote = String.fromCharCode(34, 34);
  const fmt = (d?: string | null) =>
    d ? formatAssessmentDateUtc(d, "US_SHORT") : "";
  const rows = [
    ["Assessment Report"],
    ["Title", assessment.title || assessment.name || ""],
    [
      "Launched",
      fmt(
        assessment.launchedAt ||
          assessment.launched_at ||
          assessment.start_date ||
          assessment.created_at
      ),
    ],
    [
      "Due Date",
      fmt(assessment.dueDate || assessment.due_date || assessment.due_on),
    ],
    ["Published", fmt(assessment.published_at)],
    ["Category", assessment.category || ""],
    [],
    ["Account", "Sponsor", "Account Level", "Participation", "Status"],
    ...completeAccounts.map(acc => [
      acc.name || acc.account_name || "",
      getSponsor(acc),
      getLevelCompactName(getLevel(acc) || "") || "—",
      `${acc.responses || acc.responses_received || 0}/${acc.totalAssigned || acc.requests_sent || 0} responded`,
      "Complete",
    ]),
    ...incompleteAccounts.map(acc => {
      const pl = getLevel(acc);
      return [
        acc.name || acc.account_name || "",
        getSponsor(acc),
        pl ? `Prior: ${getLevelCompactName(pl)}` : "—",
        `${acc.responses || acc.responses_received || 0}/${acc.totalAssigned || acc.requests_sent || 0} responded`,
        "Incomplete",
      ];
    }),
  ];
  const csv = rows
    .map(r =>
      r.map(c => `"${String(c).replace(/"/g, csvEscapedQuote)}"`).join(",")
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(assessment.title || assessment.name || "assessment")
    .replace(/[^a-z0-9]/gi, "-")
    .toLowerCase()}-report.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const ConsolidatedReportModal: React.FC<ConsolidatedReportModalProps> = ({
  isOpen,
  assessment,
  organizationLevels = [],
  onClose,
  onViewAccount = null,
}) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<"all" | "complete" | "incomplete">(
    "all"
  );
  const [activePieLevel, setActivePieLevel] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: "name" | "sponsor" | "level" | "participation";
    dir: "asc" | "desc";
  }>({ key: "name", dir: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [accountDetail, setAccountDetail] = useState<{
    account: Account;
    responses: ResponseItem[];
    loading: boolean;
    error: string | null;
  } | null>(null);
  const [showFlatReport, setShowFlatReport] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("all");
      setActivePieLevel(null);
      setSortConfig({ key: "name", dir: "asc" });
      setSearchQuery("");
      setAccountDetail(null);
      setShowFlatReport(false);
      setShowLogsModal(false);
    }
  }, [isOpen, assessment?.id]);

  const accounts = useMemo(
    () => (assessment?.accounts || []) as Account[],
    [assessment]
  );

  const isComplete = (acc: Account) =>
    (acc.totalAssigned || acc.requests_sent || 0) > 0 &&
    (acc.responses || acc.responses_received || 0) ===
      (acc.totalAssigned || acc.requests_sent || 0);

  const getLevel = resolveAccountLevel;

  const getSponsor = (acc: Account) => {
    if (!acc.executives?.length) return "—";
    const primary = acc.executives.find(e =>
      String(e.role || "")
        .toLowerCase()
        .includes("primary")
    );
    return (primary || acc.executives[0])?.name || "—";
  };

  const getLevelLabel = (level: string | number | null | undefined): string => {
    if (level === null || level === undefined || String(level).trim() === "") {
      return "—";
    }

    const raw = String(level).trim();
    const levelNum = getLevelNumber(raw);

    const matchedLevel = organizationLevels.find(l => {
      const code = String(l.level_code || "").trim();
      return (
        code === raw || code === `L${levelNum}` || code === String(levelNum)
      );
    });

    if (matchedLevel?.name) {
      return getAccountLevelVerboseLabel(levelNum, matchedLevel.name);
    }

    return getLevelDisplayName(raw);
  };

  const loadAccountDetail = async (account: Account) => {
    if (!assessment) return;
    setAccountDetail({ account, responses: [], loading: true, error: null });
    try {
      const assessmentId = assessment.id || assessment.assessment_id;
      const accountId = account.id || account.account_id;
      const data = await dispatch(
        fetchAccountAssessmentResponses({
          assessmentId: String(assessmentId || ""),
          accountId: String(accountId || ""),
        })
      ).unwrap();
      const responseItems = Array.isArray((data as any)?.responses)
        ? (data as any).responses
        : [];
      const responses = responseItems
        .filter((r: any) => !r?.is_draft)
        .map((r: any) => {
          const accountExecs = account.executives || [];
          const matchedExec = accountExecs.find(
            e =>
              String((e as any).id || "") === String(r.executive_id || "") ||
              String((e as any).user_id || "") === String(r.executive_id || "")
          );
          const responsePayload = r.responses || {};
          return {
            id: r.id,
            executiveName:
              r.executive?.name ||
              r.executive?.full_name ||
              matchedExec?.name ||
              matchedExec?.full_name ||
              "Unknown Executive",
            executiveEmail: r.executive?.email || matchedExec?.email || "",
            submittedAt: r.submitted_at || r.updated_at,
            calculatedLevel:
              assessment.category === "short" && responsePayload?.maturity_level
                ? responsePayload.maturity_level
                : null,
            rationale: responsePayload?.rationale || null,
            responses: responsePayload,
          };
        });
      setAccountDetail({ account, responses, loading: false, error: null });
    } catch {
      setAccountDetail({
        account,
        responses: [],
        loading: false,
        error: "Could not load responses.",
      });
    }
  };

  if (!isOpen || !assessment) return null;

  const completeAccounts = accounts.filter(isComplete);
  const incompleteAccounts = accounts.filter(a => !isComplete(a));

  const levelDistMap: Record<string, number> = {};
  completeAccounts.forEach(acc => {
    const key = getLevelKey(getLevel(acc));
    if (key) levelDistMap[key] = (levelDistMap[key] || 0) + 1;
  });

  const pieData = Object.entries(levelDistMap)
    .map(([level, count]) => ({
      level,
      name: getLevelLabel(level),
      value: count,
    }))
    .sort(
      (a, b) =>
        (parseInt(a.level.replace(/[^0-9]/g, ""), 10) || 0) -
        (parseInt(b.level.replace(/[^0-9]/g, ""), 10) || 0)
    );

  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);
  const avgLevel =
    pieTotal > 0
      ? (
          pieData.reduce(
            (s, d) =>
              s + (parseInt(d.level.replace(/[^0-9]/g, ""), 10) || 0) * d.value,
            0
          ) / pieTotal
        ).toFixed(1)
      : null;

  const handleSort = (key: "name" | "sponsor" | "level" | "participation") =>
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));

  const sortIcon = (key: "name" | "sponsor" | "level" | "participation") =>
    sortConfig.key === key ? (sortConfig.dir === "asc" ? " ↑" : " ↓") : " ↕";

  const applySort = (arr: Account[]) =>
    [...arr].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortConfig.key === "level") {
        aVal =
          parseInt(String(getLevel(a) || "").replace(/[^0-9]/g, ""), 10) || 0;
        bVal =
          parseInt(String(getLevel(b) || "").replace(/[^0-9]/g, ""), 10) || 0;
      } else if (sortConfig.key === "participation") {
        aVal =
          (a.totalAssigned || a.requests_sent || 0) > 0
            ? (a.responses || a.responses_received || 0) /
              (a.totalAssigned || a.requests_sent || 1)
            : 0;
        bVal =
          (b.totalAssigned || b.requests_sent || 0) > 0
            ? (b.responses || b.responses_received || 0) /
              (b.totalAssigned || b.requests_sent || 1)
            : 0;
      } else if (sortConfig.key === "sponsor") {
        aVal = getSponsor(a).toLowerCase();
        bVal = getSponsor(b).toLowerCase();
      } else {
        aVal = String(a.name || a.account_name || "").toLowerCase();
        bVal = String(b.name || b.account_name || "").toLowerCase();
      }
      if (aVal < bVal) return sortConfig.dir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.dir === "asc" ? 1 : -1;
      return 0;
    });

  const applySearch = (arr: Account[]) =>
    searchQuery.trim()
      ? arr.filter(a =>
          String(a.name || a.account_name || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      : arr;

  const filteredComplete = applySort(
    applySearch(
      activePieLevel
        ? completeAccounts.filter(
            a => getLevelKey(getLevel(a)) === activePieLevel
          )
        : completeAccounts
    )
  );
  const filteredIncomplete = applySort(applySearch(incompleteAccounts));

  const isAllTab = activeTab === "all";
  const isCompleteTab = activeTab === "complete";
  const isIncompleteTab = activeTab === "incomplete";
  const hasCompleteRows =
    (isAllTab || isCompleteTab) && filteredComplete.length > 0;
  const hasIncompleteRows =
    (isAllTab || isIncompleteTab) && filteredIncomplete.length > 0;

  const fmtMeta = (d?: string | null) =>
    d ? formatAssessmentDateUtc(d, "US_LONG") : null;
  const launchedDate = fmtMeta(
    assessment.launchedAt ||
      assessment.launched_at ||
      assessment.start_date ||
      assessment.created_at
  );
  const dueDateFmt = fmtMeta(
    assessment.dueDate || assessment.due_date || assessment.due_on
  );
  const publishedDate = fmtMeta(assessment.published_at);
  const levelUpdatedAt = getLatestLevelUpdateAt(assessment.notes as any);
  const updatedDate = fmtMeta(levelUpdatedAt);
  const levelUpdateLog = sortLevelUpdateLogNewestFirst(
    getLevelUpdateLog(assessment.notes as any)
  );
  const createdBy =
    assessment.createdBy ||
    assessment.created_by_name ||
    assessment.created_by_user?.name ||
    null;
  const sponsorCount = new Set(
    accounts.map(acc => getSponsor(acc)).filter(name => name && name !== "—")
  ).size;

  if (showFlatReport) {
    return (
      <FlatReportModal
        isOpen={showFlatReport}
        assessment={assessment}
        accounts={accounts}
        getLevel={getLevel}
        getLevelLabel={getLevelLabel}
        getSponsor={getSponsor}
        onClose={() => setShowFlatReport(false)}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col"
        style={{ maxHeight: "calc(100vh - 4rem)" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
                Assessment Summary Report
                <span className="inline-block bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded ml-1.5 normal-case tracking-normal align-middle">
                  NEW
                </span>
              </div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">
                {assessment.title || assessment.name || "Assessment Report"}
              </h2>
              {assessment.description && (
                <p className="text-sm text-gray-500 mt-1 max-w-xl leading-relaxed">
                  {assessment.description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {(launchedDate ||
          dueDateFmt ||
          publishedDate ||
          updatedDate ||
          assessment.category ||
          createdBy ||
          levelUpdateLog.length > 0) && (
          <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0 flex-wrap">
            <div className="flex gap-7 flex-wrap">
              {launchedDate && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Launched
                  </span>
                  <span className="text-xs text-gray-600 font-medium">
                    {launchedDate}
                  </span>
                </div>
              )}
              {dueDateFmt && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Due Date
                  </span>
                  <span className="text-xs text-gray-600 font-medium">
                    {dueDateFmt}
                  </span>
                </div>
              )}
              {publishedDate && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Published
                  </span>
                  <span className="text-xs text-gray-600 font-medium">
                    {publishedDate}
                  </span>
                </div>
              )}
              {updatedDate && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Updated
                  </span>
                  <span className="text-xs text-gray-600 font-medium">
                    {updatedDate}
                  </span>
                </div>
              )}
              {assessment.category && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Category
                  </span>
                  <span className="text-xs text-gray-600 font-medium capitalize">
                    {assessment.category}
                  </span>
                </div>
              )}
              {createdBy && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Created by
                  </span>
                  <span className="text-xs text-gray-600 font-medium">
                    {createdBy}
                  </span>
                </div>
              )}
            </div>
            {levelUpdateLog.length > 0 && (
              <button
                type="button"
                onClick={() => setShowLogsModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-purple-200 bg-white text-purple-700 text-xs font-semibold hover:bg-purple-50 flex-shrink-0"
              >
                <ScrollText size={14} />
                Logs
              </button>
            )}
          </div>
        )}

        <div className="flex border-t border-b border-gray-200 flex-shrink-0 mt-3">
          <div className="flex-none w-72 px-6 py-4 border-r border-gray-200">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Avg Level
            </div>
            <div className="text-2xl font-bold text-amber-600 leading-none">
              {avgLevel !== null ? avgLevel : "—"}
            </div>
            <div className="text-xs text-gray-400 mt-1.5">
              no prior assessment available
            </div>
          </div>
          <button
            onClick={() => {
              setActiveTab("all");
              setActivePieLevel(null);
            }}
            className={`flex-1 px-6 py-4 border-r border-gray-200 text-left transition-colors ${
              activeTab === "all" ? "bg-purple-50" : "hover:bg-gray-50"
            }`}
          >
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Accounts Targeted
            </div>
            <div className="text-2xl font-bold text-purple-600 leading-none">
              {accounts.length}
            </div>
            <div className="text-xs text-gray-400 mt-1.5">
              across {sponsorCount || "—"} sponsors
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab("complete");
              setActivePieLevel(null);
            }}
            className={`flex-1 px-6 py-4 border-r border-gray-200 text-left transition-colors ${
              activeTab === "complete" ? "bg-green-50" : "hover:bg-gray-50"
            }`}
          >
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Complete (100% participation)
            </div>
            <div className="text-2xl font-bold text-green-600 leading-none">
              {completeAccounts.length}
            </div>
            <div className="text-xs text-gray-400 mt-1.5">
              {accounts.length > 0
                ? Math.round((completeAccounts.length / accounts.length) * 100)
                : 0}
              % of targeted accounts
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab("incomplete");
              setActivePieLevel(null);
            }}
            className={`flex-1 px-6 py-4 text-left transition-colors ${
              activeTab === "incomplete" ? "bg-amber-50" : "hover:bg-gray-50"
            }`}
          >
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Incomplete
            </div>
            <div className="text-2xl font-bold text-amber-600 leading-none">
              {incompleteAccounts.length}
            </div>
            <div className="text-xs text-gray-400 mt-1.5">
              awaiting responses ·
              {accounts.length > 0
                ? Math.round(
                    (incompleteAccounts.length / accounts.length) * 100
                  )
                : 0}
              %
            </div>
          </button>
        </div>

        <div
          className="flex flex-1 overflow-hidden mt-3 border-t border-gray-200"
          style={{ minHeight: 0 }}
        >
          <div className="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col overflow-y-auto">
            <div className="p-4 flex flex-col gap-3">
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-0.5">
                  Levels Distribution
                </div>
                <div className="text-xs text-gray-400">
                  Click a slice to filter →
                </div>
              </div>
              {pieData.length > 0 ? (
                <>
                  <PieChart width={240} height={190}>
                    <Pie
                      data={pieData}
                      cx={116}
                      cy={92}
                      innerRadius={50}
                      outerRadius={78}
                      dataKey="value"
                      onClick={entry =>
                        setActivePieLevel(prev =>
                          prev === (entry as any).level
                            ? null
                            : (entry as any).level
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {pieData.map((entry, idx) => (
                        <Cell
                          key={idx}
                          fill={getSummaryPieColor(entry.level)}
                          opacity={
                            activePieLevel && activePieLevel !== entry.level
                              ? 0.25
                              : 1
                          }
                          stroke={
                            activePieLevel === entry.level ? "#1e293b" : "#fff"
                          }
                          strokeWidth={activePieLevel === entry.level ? 2 : 1}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                  <div className="flex flex-col gap-1.5">
                    {pieData.map(entry => (
                      <button
                        key={entry.level}
                        onClick={() =>
                          setActivePieLevel(prev =>
                            prev === entry.level ? null : entry.level
                          )
                        }
                        className={`flex items-center gap-2 text-left px-2 py-1 rounded transition-colors text-xs ${
                          activePieLevel === entry.level
                            ? "bg-gray-100"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: getSummaryPieColor(entry.level),
                          }}
                        />
                        <span className="flex-1 text-gray-700">
                          {entry.name}
                        </span>
                        <span className="text-gray-500 tabular-nums">
                          {entry.value} ·{" "}
                          {Math.round((entry.value / pieTotal) * 100)}%
                        </span>
                      </button>
                    ))}
                  </div>
                  {activePieLevel && (
                    <button
                      onClick={() => setActivePieLevel(null)}
                      className="text-xs text-purple-600 hover:underline text-left mt-1"
                    >
                      Clear filter ×
                    </button>
                  )}
                </>
              ) : (
                <div className="text-xs text-gray-400 italic py-4 text-center">
                  No complete accounts yet
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            {accountDetail && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 flex-shrink-0 bg-gray-50">
                  <button
                    onClick={() => setAccountDetail(null)}
                    className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:underline"
                  >
                    <ChevronLeft size={12} strokeWidth={2.5} />
                    All accounts
                  </button>
                  <span className="text-gray-300">/</span>
                  <span className="text-xs font-semibold text-gray-700 truncate">
                    {accountDetail.account.name ||
                      accountDetail.account.account_name}
                  </span>
                </div>

                <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-200 flex-shrink-0">
                  <div className="flex-1">
                    <div className="text-base font-bold text-gray-900">
                      {accountDetail.account.name ||
                        accountDetail.account.account_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {accountDetail.account.responses ||
                        accountDetail.account.responses_received ||
                        0}
                      /
                      {accountDetail.account.totalAssigned ||
                        accountDetail.account.requests_sent ||
                        0}{" "}
                      responded
                    </div>
                  </div>
                  {(() => {
                    const finalLvl = getLevel(accountDetail.account);
                    return finalLvl ? (
                      <div className="flex flex-col items-end gap-1">
                        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                          Final Account Level
                        </div>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getLevelPillClass(
                            finalLvl
                          )}`}
                        >
                          {getLevelLabel(finalLvl)}
                        </span>
                      </div>
                    ) : null;
                  })()}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                  {accountDetail.loading && (
                    <div className="flex items-center justify-center py-12 text-sm text-gray-400">
                      Loading responses…
                    </div>
                  )}
                  {accountDetail.error && (
                    <div className="text-sm text-red-500 py-4">
                      {accountDetail.error}
                    </div>
                  )}
                  {!accountDetail.loading &&
                    !accountDetail.error &&
                    accountDetail.responses.length === 0 && (
                      <div className="text-sm text-gray-400 italic py-4 text-center">
                        No responses submitted yet for this account.
                      </div>
                    )}
                  {!accountDetail.loading &&
                    accountDetail.responses.map((resp, idx) => {
                      const lvl = resp.calculatedLevel;
                      const rationale =
                        resp.rationale || resp.responses?.rationale;
                      const otherAnswers = resp.responses
                        ? Object.entries(resp.responses).filter(
                            ([k]) => k !== "maturity_level" && k !== "rationale"
                          )
                        : [];
                      const fmtLabel = (key: string) =>
                        key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, l => l.toUpperCase());
                      return (
                        <div
                          key={resp.id || idx}
                          className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50/50"
                        >
                          <div className="flex items-center gap-3 px-5 py-4 bg-gray-50">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-800 truncate">
                                {resp.executiveName}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {resp.executiveEmail}
                              </div>
                            </div>
                            {lvl && (
                              <span
                                className={`flex-shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getLevelPillClass(
                                  lvl
                                )}`}
                              >
                                {getLevelLabel(lvl)}
                              </span>
                            )}
                          </div>
                          <div className="px-5 py-4 space-y-2">
                            {rationale ? (
                              <div>
                                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                                  Rationale
                                </div>
                                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                  {rationale}
                                </p>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 italic">
                                No rationale provided.
                              </p>
                            )}
                            {otherAnswers
                              .filter(([, v]) => v)
                              .map(([key, val]) => (
                                <div key={key}>
                                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                                    {fmtLabel(key)}
                                  </div>
                                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {val}
                                  </p>
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                  <button
                    onClick={() => setAccountDetail(null)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    ← Back to all accounts
                  </button>
                  {onViewAccount && (
                    <button
                      onClick={() => {
                        onViewAccount(assessment, accountDetail.account);
                        onClose();
                      }}
                      className="text-xs text-purple-600 hover:underline font-medium"
                    >
                      Open full report →
                    </button>
                  )}
                </div>
              </div>
            )}

            {!accountDetail && (
              <>
                {activePieLevel && (
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-purple-50 border-b border-purple-200 text-xs flex-shrink-0">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: getSummaryPieColor(activePieLevel),
                      }}
                    />
                    <span className="flex-1 text-purple-700">
                      Filtered by pie:{" "}
                      <strong>{getLevelLabel(activePieLevel)}</strong>
                    </span>
                    <button
                      onClick={() => setActivePieLevel(null)}
                      className="text-purple-400 hover:text-purple-600 font-semibold text-sm leading-none"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 flex-shrink-0 gap-3">
                  <div className="flex gap-0.5 bg-gray-100 border border-gray-200 rounded-lg p-1">
                    {[
                      { id: "all", label: "All", count: accounts.length },
                      {
                        id: "complete",
                        label: "Complete",
                        count: completeAccounts.length,
                      },
                      {
                        id: "incomplete",
                        label: "Incomplete",
                        count: incompleteAccounts.length,
                      },
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(
                            tab.id as "all" | "complete" | "incomplete"
                          );
                          setActivePieLevel(null);
                        }}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          activeTab === tab.id
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {tab.label}{" "}
                        <span
                          className={`ml-0.5 ${
                            activeTab === tab.id
                              ? "text-purple-600"
                              : "text-gray-400"
                          }`}
                        >
                          {tab.count}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 ml-auto">
                    <button
                      onClick={() => setShowFlatReport(true)}
                      className="flex items-center gap-1.5 text-sm font-semibold text-purple-600 hover:underline whitespace-nowrap"
                    >
                      <FileText size={13} strokeWidth={2} />
                      Consolidated Responses
                    </button>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search accounts…"
                      className="w-40 text-xs border border-gray-200 rounded-md px-2.5 py-1.5 bg-gray-50 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto mt-3 border-t border-gray-200">
                  {!hasCompleteRows && !hasIncompleteRows ? (
                    <div className="text-center py-10 text-sm text-gray-400">
                      No accounts to show
                    </div>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          {[
                            { key: "name", label: "Account" },
                            { key: "sponsor", label: "Sponsor" },
                            { key: "level", label: "Account Level" },
                            { key: "participation", label: "Participation" },
                          ].map(col => (
                            <th
                              key={col.key}
                              className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap"
                              onClick={() =>
                                handleSort(
                                  col.key as
                                    | "name"
                                    | "sponsor"
                                    | "level"
                                    | "participation"
                                )
                              }
                            >
                              {col.label}
                              {sortIcon(
                                col.key as
                                  | "name"
                                  | "sponsor"
                                  | "level"
                                  | "participation"
                              )}
                            </th>
                          ))}
                          <th className="px-4 py-2.5 w-16" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="!border-0">
                          <td colSpan={5} className="h-3 p-0" />
                        </tr>
                        {hasCompleteRows && (
                          <>
                            {isAllTab && (
                              <tr className="bg-gray-100">
                                <td
                                  colSpan={5}
                                  className="px-4 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider"
                                >
                                  Complete — 100% Participation (
                                  {filteredComplete.length} account
                                  {filteredComplete.length !== 1 ? "s" : ""})
                                </td>
                              </tr>
                            )}
                            {filteredComplete.map((account, idx) => {
                              const level = getLevel(account);
                              return (
                                <tr
                                  key={`c-${idx}`}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px]">
                                    <span className="truncate block">
                                      {account.name || account.account_name}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 max-w-[140px]">
                                    <span className="truncate block">
                                      {getSponsor(account)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    {level ? (
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getLevelPillClass(
                                          level
                                        )}`}
                                      >
                                        {getLevelLabel(level)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 text-xs">
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-green-100 text-green-700 tabular-nums">
                                      {account.responses ||
                                        account.responses_received ||
                                        0}
                                      /
                                      {account.totalAssigned ||
                                        account.requests_sent ||
                                        0}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <button
                                      onClick={() => loadAccountDetail(account)}
                                      className="text-xs text-purple-600 hover:underline whitespace-nowrap"
                                    >
                                      View →
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        )}

                        {hasIncompleteRows && (
                          <>
                            {isAllTab && (
                              <tr className="bg-amber-50 border-t border-amber-200">
                                <td
                                  colSpan={5}
                                  className="px-4 py-1.5 text-[11px] font-semibold text-amber-700 uppercase tracking-wider"
                                >
                                  Incomplete — Not in Levels Mix (
                                  {filteredIncomplete.length} account
                                  {filteredIncomplete.length !== 1 ? "s" : ""})
                                </td>
                              </tr>
                            )}
                            {filteredIncomplete.map((account, idx) => {
                              const priorLevel = getLevel(account);
                              const responded =
                                account.responses ||
                                account.responses_received ||
                                0;
                              const total =
                                account.totalAssigned ||
                                account.requests_sent ||
                                0;
                              const pct =
                                total > 0
                                  ? Math.round((responded / total) * 100)
                                  : 0;
                              const badgeColor =
                                pct >= 50
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700";
                              return (
                                <tr
                                  key={`i-${idx}`}
                                  className="bg-amber-50/40 hover:bg-amber-50"
                                >
                                  <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px]">
                                    <span className="truncate flex items-center gap-1">
                                      <span className="text-amber-500">⚠</span>
                                      {account.name || account.account_name}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 max-w-[140px]">
                                    <span className="truncate block">
                                      {getSponsor(account)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    {priorLevel ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                        Prior: {getLevelLabel(priorLevel)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 text-xs">
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold tabular-nums ${badgeColor}`}
                                    >
                                      {responded}/{total}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <button
                                      onClick={() => loadAccountDetail(account)}
                                      className="text-xs text-purple-600 hover:underline whitespace-nowrap"
                                    >
                                      View →
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <FlatReportModal
          isOpen={showFlatReport}
          assessment={assessment}
          accounts={accounts}
          getLevel={getLevel}
          getLevelLabel={getLevelLabel}
          getSponsor={getSponsor}
          onClose={() => setShowFlatReport(false)}
        />

        {showLogsModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowLogsModal(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Update logs
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Override and disconnect accept events by account
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLogsModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="overflow-y-auto px-5 py-4 space-y-3">
                {levelUpdateLog.map((entry, idx) => (
                  <div
                    key={entry.id || `${entry.account_id}-${entry.at}-${idx}`}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {entry.account_name || "Account"}
                        </div>
                        <div className="text-xs text-purple-700 font-medium mt-0.5">
                          {levelUpdateEventLabel(entry.type)}
                          {entry.level
                            ? ` · ${getLevelDisplayName(entry.level)}`
                            : ""}
                          {entry.combined ? (
                            <span className="ml-1.5 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                              Combined
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-500 whitespace-nowrap flex-shrink-0">
                        {entry.at ? formatAssessmentDateTimeUtc(entry.at) : "—"}
                      </div>
                    </div>
                    {entry.actor_name && (
                      <div className="text-xs text-gray-600 mt-1.5">
                        By {entry.actor_name}
                      </div>
                    )}
                    {entry.rationale?.trim() && (
                      <p className="text-xs text-gray-700 mt-2 whitespace-pre-wrap border-t border-gray-200 pt-2">
                        {entry.rationale.trim()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-gray-200 flex justify-end flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowLogsModal(false)}
                  className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50 rounded-b-xl">
          <button
            onClick={() =>
              exportSummaryCSV(
                assessment,
                completeAccounts,
                incompleteAccounts,
                getLevel,
                getSponsor
              )
            }
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <Download size={12} strokeWidth={2} />
            Export CSV
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export { FlatReportModal };
export default ConsolidatedReportModal;
